<script>
import axios from 'axios';
import { FormButton, SelectField } from '../form';
import CheckboxInput from '../form/CheckboxInput.vue';
import PaginationTable from '../PaginationTable.vue';
import TextInput from '../form/TextInput.vue';
import ModalDialog from '../ModalDialog.vue';
import { GlobalPolicy } from '@/policies/global';
import SearchInput from '../form/SearchInput.vue';

const query = {
    search: '',
    collection: null
}

export default {
    setup() {
        return {
            GlobalPolicy,
            panes: [
                {
                    name: 'collection',
                    endpoint: 'collections',
                    label: 'Collections',
                    category: 'community'
                }
            ]
        }
    },
    data() {
        return {
            attached: {
                collection: []
            },
            collections: [],
            items: [],
            query: {...query},
            context: null,
            selected: []
        }
    },
    methods: {
        index() {
            axios.get(`agents/${this.id}/collections`)
            .then(resp => {
                this.attached.collection = resp.data;
            })
            .catch(err => {
                console.error(err);
            });
        },
        openAttach(context) {
            this.context = context;
            this.indexItems();
        },
        indexCollections() {
            axios.get('collections')
            .then(resp => {
                this.collections = resp.data.sort((a,b) => a.meta?.info?.name > b.meta?.info?.name ? 1 : -1);
            })
            .catch(err => {
                console.error(err);
            });
        },
        indexItems() {
            if(!this.context) return;
            this.items = this.collections
        },
        cancel() {
            this.context = null;
            this.items = [];
            this.selected = [];
            this.query = {...query};
        },
        sync() {
            axios.post(`agents/${this.id}/collections`, {items: this.selected})
            .then(resp => {
                this.attached[this.context.name] = resp.data;
                this.cancel();
            })
            .catch(err => {
                console.error(err);
            })
        },
        unsync(item, context) {
            axios.delete(`agents/${this.id}/collections/${item.id}`)
            .then(resp => {
                this.attached[context] = this.attached[context].filter(attached => attached.id !== item.id);
            })
            .catch(err => {
                console.error(err);
            })
        },
        getCollection(rid) {
            return this.collections.find(collection => collection.community_rid === rid) || '-';
        }
    },
    computed: {
        filtered() {
            let ids = this.attached[this.context.name].map(attach => attach.id);
            let items = this.items.filter(item => !ids.includes(item.id) && item.status !== 'inactive');

            if(this.query.search) {
                items = items.filter(item => JSON.stringify(item).toLowerCase().includes(this.query.search.toLowerCase()));
            }

            if(this.query.collection) {
                items = items.filter(item => item.kova.CommunityRID == this.query.collection);
            }

            return items;
        },
        mainColumns() {
            return [
                {
                    name: 'name',
                    label: 'Name',
                    sort: (row) => row.meta?.info?.name || '',
                    default: true
                },
                {
                    name: 'actions',
                    label: '',
                    sort: false,
                    default: true,
                    class: 'text-right'
                }
            ];
        },
        modalColumns() {
            let cols = [
                {
                    name: 'select',
                    label: '',
                    sort: false,
                    default: true
                },
                {
                    name: 'name',
                    label: 'Name',
                    sort: (row) => this.context?.name == 'sales_order' ? row.kova?.Lot?.StreetAddress || '' : row.meta?.info?.name || row.kova?.Name || '',
                    default: true
                }
            ];

            if (this.context && ['floorplan','sales_order'].includes(this.context.name)) {
                cols.push({
                    name: 'collection',
                    label: 'Collection',
                    sort: (row) => this.getCollection(row.kova?.CommunityRID)?.meta?.info?.name || '',
                    default: true
                });
            }

            return cols;
        }
    },
    props: {
        id: {
            type: Number,
            required: true
        }
    },
    created() {
        this.index();
        this.indexCollections();
    },
    components: {
        CheckboxInput,
        FormButton,
        SelectField,
        TextInput,
        ModalDialog,
        PaginationTable,
        SearchInput
    }
}
</script>

<template>
    <h2 class="text-xl text-bhav-blue-600 font-medium">Attached Locations</h2>
    <template v-for="pane, i in panes" :key="`pane-${i}`">
        <div class="flex justify-between items-center mt-8">
            <h3 class="text-lg text-oakwood-red-600 font-medium">{{ pane.label }}</h3>
            <div>
                <FormButton v-if="GlobalPolicy.edit()" @click="openAttach(pane)" icon="fa-plus" prefix-class="fak" color="blue">
                    Bulk Attach
                </FormButton>
            </div>
        </div>
        <PaginationTable empty-message="There are currently no attachments." :data="attached[pane.name]" :columns="mainColumns" :view="`agent-${pane.name}`" :legacy="false" :default="5">
            <template v-if="pane.name === 'collection'" #col-name="{row}">
                <router-link :to="{name: 'collection-tab', params: {id: row.id, tab: 'agents'}}" class="text-bhav-blue-600 py-4 block">
                    {{ row.meta?.info?.name }}
                </router-link>
            </template>

            <template v-if="pane.name === 'collection'" #col-actions="{row}">
                <div class="py-4 flex gap-2 justify-end">
                    <FormButton v-if="GlobalPolicy.edit()" @click="unsync(row, pane.name)" title="Detach the image from this item" icon="fa-detach" prefix-class="fak" color="red">
                        <span class="sr-only">Detach</span>
                    </FormButton>
                </div>
            </template>
        </PaginationTable>
    </template>

    <ModalDialog v-if="context" :open="context !== null" @close="cancel">
        <template #header>
            <h2>Bulk Attach {{ context.label }}</h2>
        </template>
        <template #body>
            <div class="flex items-center gap-4">
                <div v-if="['floorplan','sales_order'].includes(context.name)">
                    <SelectField v-model="query.collection" label="Collection" :options="collections.map(collection => ({label: collection.meta?.info?.name, value: collection.community_rid}))" empty="All Collections" :allow-null="true" />
                </div>
                <div>
                    <SearchInput v-model="query.search" placeholder="Search by keyword" />
                </div>
            </div>
            <PaginationTable empty-message="There are currently no attachments." :data="filtered" :columns="modalColumns" :view="`agent-attach-${context.name}`" :legacy="false" :default="5">
                <template #col-select="{row}">
                    <CheckboxInput :showLabel="false" :label="row.meta?.info?.name || ''" v-model="selected" :value="row.id" />
                </template>

                <template #col-name="{row}">
                    <div>
                        {{ context.name == 'sales_order' ? `${row.kova.Lot.StreetAddress}` : row.meta?.info?.name || row.kova.Name }}
                    </div>
                </template>

                <template #col-collection="{row}">
                    <div>
                        {{ getCollection(row.kova?.CommunityRID)?.meta?.info?.name }}
                    </div>
                </template>
            </PaginationTable>
        </template>
        <template #buttons>
            <FormButton @click="cancel" color="gray">Cancel</FormButton>
            <FormButton @click="sync" :disabled="!selected.length" color="blue">Attach to Selected</FormButton>
        </template>
    </ModalDialog>
</template>